const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Endpoints keyed by an opaque slug rather than a numeric id. These need an explicit rule:
// `Generator::Run#slug` is `<title>-<urlsafe_base64>`, so the trailing segment is unbounded and
// every failing model would otherwise open its own Sentry issue. A generic "segment contains a
// digit" heuristic cannot stand in for this list - it both misses digit-free base64 tokens and
// wrongly collapses static segments such as `publishing-score-v2`. Add a route here when a new
// slug-keyed endpoint appears.
const SLUG_KEYED_ROUTES = [
  /^(\/api\/internal\/generator\/community-models)\/[^/]+$/,
]

const stripQuery = (path) => String(path || '').split('?')[0]

// Axios rejections stack entirely inside `settle`, so Sentry's default grouping collapses every
// failed request in the app into one issue (MARKETPLACE-JS-8HCQ: 20k events, no request context,
// un-triageable). Fingerprinting on method + route + status splits it per endpoint.
export function normalizePath(path) {
  const withoutQuery = stripQuery(path)

  const slugRoute = SLUG_KEYED_ROUTES.reduce(
    (matched, pattern) => matched || withoutQuery.match(pattern),
    null
  )
  if (slugRoute) return `${slugRoute[1]}/:slug`

  return withoutQuery
    .split('/')
    .map((segment) => {
      if (/^\d+$/.test(segment) || UUID_SEGMENT.test(segment)) return ':id'
      return segment
    })
    .join('/')
}

export function apiErrorReport(method, path, error) {
  const status = error?.response?.status
  const route = normalizePath(path)
  const statusTag = status ? String(status) : 'network'

  return {
    fingerprint: ['api-error', method, route, statusTag],
    tags: {
      'api.method': method,
      'api.route': route,
      'api.status': statusTag,
    },
    // Query string and response body are both dropped deliberately. Query strings carry
    // credentials (`useSubscriptionCheckout` appends the verified `MTCaptchaToken` to the path)
    // and response bodies are arbitrary - a 422 echoes back whatever the user submitted. Route,
    // method and status are what triage needs; neither may cost us PII in Sentry.
    extra: {
      path: stripQuery(path),
    },
  }
}
